/**
 * paytableConfig.js — Paytable overlay configuration and helpers
 *
 * Pure JS module (no React). All device configs, layout ratios,
 * content scaling, helper functions, and module-level texture caches.
 *
 * Consumed by PaytableOverlay.jsx, PaytableSlider.jsx, and slide components.
 */

import { PixiSettings } from "../../../../pixiSettings";
import { symbolData } from "../../../../SymbolData";
import { formatToCustomLocale } from "../../../../utilities";
import { getGameImageSrc } from "../../../../assets";

/**
 * Resolve an image src from the PIXI Assets cache (preloaded during loading screen).
 * Alias for getGameImageSrc — use assetName in config instead of raw img paths.
 */
export const getAssetSrc = getGameImageSrc;

// ---------------------------------------------------------------------------
// Animation timing (matching primary UI pattern)
// ---------------------------------------------------------------------------
export const OVERLAY_FADE_DURATION = 200;
export const CONTENT_ANIM_DURATION = 250;

/**
 * Visible-height gate for the "cramped portrait" paytable/rules branch.
 * Alone this is not enough — fullscreen tall phones can report < 820 CSS px
 * while still having nearly the full screen available to the game.
 */
export const SHORT_PORTRAIT_HEIGHT = 820;

/**
 * Minimum gap (px) between full screen/layout height and game-visible height
 * before we treat the viewport as browser-toolbar-shortened.
 * Fullscreen (game ≈ screen) → gap ~0 → keep standard-mobile layout.
 */
export const BROWSER_CHROME_STOLEN_MIN_PX = 80;

/**
 * True when a standard-mobile portrait viewport is shortened by browser chrome
 * (toolbars), not merely "under SHORT_PORTRAIT_HEIGHT".
 *
 * Compares game-visible height (`dimensions.height` / visualViewport) to the
 * larger of `window.innerHeight` and the screen's long side. Only when the
 * browser clearly stole space AND visible height is still under the short gate
 * do we route to the small-mobile portrait shell.
 *
 * @param {number} visibleHeight - Game / visualViewport height in CSS px
 * @returns {boolean}
 */
export function isBrowserChromeShortenedPortrait(visibleHeight) {
  if (!(visibleHeight < SHORT_PORTRAIT_HEIGHT)) return false;
  if (typeof window === "undefined") return true;

  const layoutHeight = window.innerHeight || visibleHeight;
  const screenLong = Math.max(
    window.screen?.width ?? 0,
    window.screen?.height ?? 0,
  );
  const fullHeight = Math.max(layoutHeight, screenLong);
  const stolenByBrowserChrome = Math.max(0, fullHeight - visibleHeight);

  return stolenByBrowserChrome >= BROWSER_CHROME_STOLEN_MIN_PX;
}

/**
 * Device/orientation buckets shared by the secondary-UI overlays (paytable,
 * game rules, replay). Single source for the standard/small-mobile + tablet
 * split and the toolbar-shortened-portrait → small fallback, so the overlays
 * can't drift out of lock-step with getPaytableShellLayout's own branching.
 *
 * @param {{device: string, orientation: string, isMobileSmall: boolean,
 *   dimensions: {height: number}}} ctx
 * @returns {{isDesktop: boolean, isPortrait: boolean,
 *   isTabletPortrait: boolean, isTabletLandscape: boolean,
 *   isStandardMobilePortrait: boolean, isStandardMobileLandscape: boolean,
 *   isSmallMobilePortrait: boolean, isSmallMobileLandscape: boolean}}
 */
export const getOverlayDeviceBuckets = ({
  device,
  orientation,
  isMobileSmall,
  dimensions,
}) => {
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Toolbar/browser-chrome-shortened standard portrait falls back to the
  // small-mobile-portrait bucket so content isn't cut off.
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  return {
    isDesktop,
    isPortrait,
    isTabletPortrait: isTablet && isPortrait,
    isTabletLandscape: isTablet && !isPortrait,
    isStandardMobilePortrait:
      isStandardMobile && isPortrait && !isShortStandardPortrait,
    isStandardMobileLandscape: isStandardMobile && !isPortrait,
    isSmallMobilePortrait:
      isPortrait && (isMobileSmall || isShortStandardPortrait),
    isSmallMobileLandscape: isMobileSmall && !isPortrait,
  };
};

// Navigation throttle — prevents rapid-fire from automated tools or accessibility
export const NAV_THROTTLE_MS = 100;

// Debug wireframe colors — one per slide so positions are visible
export const DEBUG_SLIDE_COLORS = [
  "#ff0000",
  "#0088ff",
  "#00cc00",
  "#ffdd00",
  "#ff00ff",
  "#00cccc",
  "#ff8800",
  "#88ff00",
  "#ff4488",
  "#ffffff",
];

// ---------------------------------------------------------------------------
// Slide content data
// ---------------------------------------------------------------------------
// ── The WILD-SOLO slide is gone ──
//
// It showed one symbol on its own, and Mad Labs shows the wild beside the
// scatter on the slide below instead — two symbols that behave alike, on one
// screen, rather than a page for each.
//
// Removing the ENTRY is what removes the slide: the count, the dots and the
// wrap all come off SLIDE_TITLES.length, so nothing else counts slides. The
// index branches in PaytableOverlay's map shifted down by one with it.
// export const SLIDE_TITLES = [
//   "paytable_symbols_title", // 0: Wild + Scatter
//   "paytable_symbols_title", // 1: Regular symbols grid
//   "paytable_features_title", // 2: Gamble round
//   "paytable_features_title", // 3: Collect Feature
//   "paytable_features_title", // 4: Double Feature
//   "paytable_features_title", // 5: Mystery Feature
//   "paytable_paylines_title", // 6: Payline diagrams
// ];
// Slide 1 (index 1) is now the Hold & Win symbols — Scarab, Chest, Golden
// Chest (Vladimir, 2026-09-30) — and everything after it moved down by one.
// export const SLIDE_TITLES = [
//   "paytable_symbols_title", // 0: Wild + Scatter
//   "paytable_symbols_title", // 1: Scarab + Chest + Golden Chest
//   "paytable_symbols_title", // 2: Regular symbols grid
//   "paytable_features_title", // 3: Gamble round
//   "paytable_features_title", // 4: Collect Feature
//   "paytable_features_title", // 5: Double Feature
//   "paytable_features_title", // 6: Mystery Feature
//   "paytable_paylines_title", // 7: Payline diagrams
// ];
// Slide index 2 is now the coins — Coin + Jackpot Symbols (2026-09-30).
// export const SLIDE_TITLES = [
//   "paytable_symbols_title", // 0: Wild + Scatter
//   "paytable_symbols_title", // 1: Scarab + Chest + Golden Chest
//   "paytable_symbols_title", // 2: Coin + Jackpot Symbols
//   "paytable_symbols_title", // 3: Regular symbols grid
//   "paytable_features_title", // 4: Gamble round
//   "paytable_features_title", // 5: Collect Feature
//   "paytable_features_title", // 6: Double Feature
//   "paytable_features_title", // 7: Mystery Feature
//   "paytable_paylines_title", // 8: Payline diagrams
// ];
// Index 5 is a NEW slide — Unlock more rows + Royal Jackpot (2026-09-30);
// the feature slides after it (FEATURE_SLIDES[1..3]) moved down by one.
// export const SLIDE_TITLES = [
//   "paytable_symbols_title", // 0: Wild + Scatter
//   "paytable_symbols_title", // 1: Scarab + Chest + Golden Chest
//   "paytable_symbols_title", // 2: Coin + Jackpot Symbols
//   "paytable_symbols_title", // 3: Regular symbols grid
//   "paytable_features_title", // 4: Cleopatra's Respins (FEATURE_SLIDES[0])
//   "paytable_features_title", // 5: Unlock more rows + Royal Jackpot (SYMBOL_SLIDE_ROWS)
//   "paytable_features_title", // 6: Free Spins (FEATURE_SLIDES[1])
//   "paytable_features_title", // 7: FEATURE_SLIDES[2]
//   "paytable_features_title", // 8: FEATURE_SLIDES[3]
//   "paytable_paylines_title", // 9: Payline diagrams
// ];
// Cleopatra's Fire's own set (Vladimir, 2026-09-30): Mad Labs' Golden Reveal
// and Mystery Reveal slides are gone, Free Spins is slide 1's layout with one
// item, and the win lines close the paytable.
export const SLIDE_TITLES = [
  "paytable_symbols_title", // 0: Wild + Scatter
  "paytable_symbols_title", // 1: Scarab + Chest + Golden Chest
  "paytable_symbols_title", // 2: Coin + Jackpot Symbols
  "paytable_symbols_title", // 3: Regular symbols grid
  "paytable_features_title", // 4: Cleopatra's Respins (FEATURE_SLIDES[0])
  "paytable_features_title", // 5: Unlock more rows + Royal Jackpot (SYMBOL_SLIDE_ROWS)
  "paytable_features_title", // 6: Free Spins (FEATURE_SLIDES[1] — the Respins layout)
  "paytable_paylines_title", // 7: Payline diagrams
];

export const TOTAL_SLIDES = SLIDE_TITLES.length;

/**
 * How wide the DESCRIPTION may run in the landscape column, as a percent of the
 * slide body.
 *
 * The row layout constrained it for free — `.spt-feature-text-wrap` is 50% of
 * the slide, so the text could never span more than half. The column has no
 * such wrapper, so without a cap the description runs the full width and the
 * slide reads as one long line of text under a small picture.
 *
 * Read in TWO places and they must agree: the CSS `max-width` that draws it,
 * and the fit-to-height measurement that picks the font size. Measure a wider
 * box than you draw and the text is sized for room it does not have.
 *
 * Landscape column only — portrait already fills its width by design.
 */
export const STACKED_LANDSCAPE_DESC_MAX_W_PCT = 68;

export const SLIDE_CONTENT_SPACED_GAP_MUL = {
  mobilePortrait: 3,
  mobileSmallPortrait: 3,
  mobileLandscape: 1,
  default: 4,
};

// Standard mobile landscape — uniform feature subtitle + description sizing (all slides)
export const STANDARD_MOBILE_LANDSCAPE_TYPO = {
  featureSubtitleMul: 1.35,
  descFontMul: 1.1,
  fixedDescFont: true,
  // Small mobile landscape — extra shrink on feature-slide descriptions so
  // the longest translations still fit (11px → 10px at this breakpoint).
  smallMobileLandscapeDescFontMul: 0.9,
};

// Small mobile portrait — uniform description + subtitle bumps
export const SMALL_MOBILE_PORTRAIT_TYPO = {
  descFontMul: 13 / 12, // 12px → 13px at typical base
  subtitleFontMul: 16 / 15, // 15px → 16px at typical base
};

export const FEATURE_SLIDES = [
  {
    subtitle: "paytable_features_gamble_title",
    // assetName: "gamble",
    // Cleopatra's Respins (the texts under the old gamble keys) — the
    // scatter's own picture for now, from its spine (Vladimir, 2026-09-30).
    // symbolName: "sc",
    // The art (Vladimir, 2026-10-02): the intro's Hold & Win board.
    assetName: "startpage_image_1",
    // Long translations (e.g. Russian) can push this title past its wrap's
    // width in landscape — keep it on one line and don't clip it.
    subtitleNoWrap: true,
    imgWidth: {
      mobilePortrait: "90%",
      mobileLandscape: "70%",
      desktop: "70%",
    },
    descFontMul: 0.8,
    descWidthMultipliers: [19, 16, 18],
    textPadMul: 0.5,
    // landscapeImgMul: 1.3,
    // ── Landscape: the board as wide as Free Spins' (Loc QA pass,
    // 2026-10-28: "enlarge it so it resembles the second one") ── this art's
    // board is 63% of its canvas (the rest is glow), Free Spins' 84%, so it
    // needs x1.53 of Free Spins' 1.5 = 2.0. Desktop draws as mobile landscape
    // here (PaytableOverlay's layoutDevice), so `desktop:` keys never apply.
    landscapeImgMul: 2.0,
    // Taller than its box at that size — let it out (see imgOverflowVisible).
    imgOverflowVisible: true,
    // Was -4% on mobileLandscape, which pulled the machine up against the
    // title. Zero for now — the flex layout's own placement is what we are
    // testing. Remember `mobileLandscape` is EVERY non-desktop landscape,
    // phones and tablets both; `desktop` is a separate key that this slide
    // does not set at all, so a desktop gets no margin either way.
    // imgMarginTop: { mobileLandscape: "0%" },
    // Lifted so the board's top meets the title's top line (Loc QA pass,
    // 2026-10-28) — its board starts 16.7% down the canvas.
    imgMarginTop: { mobileLandscape: "-10.6%" },
    landscapeBodyGapMul: 0.8,
    // 1.5x the shared linePad between title, image and description — portrait
    // only, this slide only. Covers standard AND small mobile portrait: they
    // both resolve through the same `mobilePortrait` bucket, and `linePad` is
    // itself derived from the title size, so the extra air scales with the
    // device rather than being a fixed pixel gap.
    portraitBodyGapMul: 1.5,
    // Portrait: the board is only 63.5% of this art's height (the rest glow)
    // and drew ~135 px in its band (Loc QA pass, 2026-10-28: "lets make it
    // have nice ratio"). Sized BY THE BAND so every phone, small ones too,
    // shows the same proportions: 134% of it puts the board at ~85% of the
    // band, clear of title and text; the glow runs past, behind them.
    imgFillsBand: 134,
    // stackedBodyMarginTop is GONE. It was -5% in portrait, pulling the whole
    // stacked body — image and description together — up under the title. The
    // slide does not need it, and the title's own spacing reads correctly
    // without it.
    //
    // (Its `tabletPortrait: -8%` never did anything: getImgWidth reads only
    // mobilePortrait / mobileLandscape / desktop, and folds tablet into mobile.)
    desc: "paytable_features_gamble_description",
  },
  // ── Free Spins — IDENTICAL to Cleopatra's Respins above, only its texts
  // differ (Vladimir, 2026-09-30: "just translation difference") ──
  {
    // subtitle: "paytable_features_free_spins_title",
    subtitle: "freespins", // shared translation (2026-10-13)
    // symbolName: "sc",
    // The art (Vladimir, 2026-10-02): the intro's free-spins board.
    assetName: "startpage_image_4",
    subtitleNoWrap: true,
    imgWidth: {
      mobilePortrait: "90%",
      mobileLandscape: "70%",
      desktop: "70%",
    },
    descFontMul: 0.8,
    descWidthMultipliers: [19, 16, 18],
    textPadMul: 0.5,
    // landscapeImgMul: 1.3,
    // Landscape a bit bigger (x1.15), and lifted so the board's top meets the
    // title's top line (Loc QA pass, 2026-10-28, against his ruler). The
    // margin is a share of the body's WIDTH (CSS), so it scales with it.
    landscapeImgMul: 1.5,
    // imgMarginTop: { mobileLandscape: "0%" },
    imgMarginTop: { mobileLandscape: "-3.3%" },
    imgOverflowVisible: true,
    landscapeBodyGapMul: 0.8,
    portraitBodyGapMul: 1.5,
    // Portrait: sized BY THE BAND, as Respins above (Loc QA pass, 2026-10-28:
    // "image can be bigger"). This art is wide with little glow (board 84% x
    // 81%), so the full band — width-limited by the column — is enough: the
    // board about as big as Respins', all of it inside, on every phone.
    imgFillsBand: 100,
    desc: "paytable_features_free_spins_description",
  },
  // Mad Labs' Free Spins / Golden Reveal / Mystery Reveal feature slides —
  // removed from Cleopatra's Fire (2026-09-30). Free Spins is SYMBOL_SLIDE_FS.
  // {
  //   subtitle: "paytable_features_free_spins_title",
  //   // ── COLUMN in every layout: title, image, text ──
  //   //
  //   // Client reference, 2026-09-09. Collect / Double / Mystery all read as one
  //   // centred column on landscape and desktop too, not image-left / text-right.
  //   // The GAMBLE slide above keeps the row layout — it was signed off as it is.
  //   //
  //   // The stacked markup and its CSS were already device-agnostic, so this only
  //   // switches which branch renders. See `stacked` in PaytableOverlay.
  //   stackedAlways: true,
  //   // Percent of the portrait band the picture fills, instead of being sized by
  //   // its width — see imgFillsBand in PaytableOverlay. 100 was a shade too big;
  //   // 90 leaves a little air at the edges. The band and the description line
  //   // are unaffected either way — only the picture inside changes.
  //   imgFillsBand: 90,
  //   // ONE image, not the split pot/coins pair — see the composite branch in
  //   // PaytableOverlay, which only runs when potAssetName is present.
  //   // Shared with the intro popup so the two screens show the same art.
  //   assetName: "startpage_image_1",
  //   imgWidth: {
  //     mobilePortrait: "75%",
  //     mobileLandscape: "55%",
  //     desktop: "55%",
  //   },
  //   descFontMul: 0.8,
  //   descWidthMultipliers: [15],
  //   // Same 1.5x portrait gaps as the Gamble slide — see the note there. All
  //   // four feature slides now breathe alike in the column.
  //   portraitBodyGapMul: 1.5,
  //   landscapeImgMul: 1.3,
  //   // Portrait only. The -3% landscape nudge was tuned for the row layout and
  //   // is wrong in the column — the image is centred in its own box now and
  //   // needs no pull.
  //   // ── NO portrait margins: they are what broke the shared line ──
  //   //
  //   // These three carried imgMarginTop -3.5% and their own descMarginTop
  //   // (-4% / -7.5% / -3.5%), while the Gamble slide carried neither. Every one
  //   // of them shifts this slide's image band or its description relative to
  //   // that slide, which is exactly the mismatch the ruler showed.
  //   //
  //   // The band (STACKED_PORTRAIT_IMG_BAND) is what positions these now, and it
  //   // only holds the four slides on one line if nothing nudges them afterwards.
  //   // Do not reintroduce a per-slide portrait margin here — tune the band.
  //   desc: "paytable_features_free_spins_description",
  // },
  // {
  //   subtitle: "paytable_features_golden_reveal_title",
  //   // ── COLUMN in every layout: title, image, text ──
  //   //
  //   // Client reference, 2026-09-09. Collect / Double / Mystery all read as one
  //   // centred column on landscape and desktop too, not image-left / text-right.
  //   // The GAMBLE slide above keeps the row layout — it was signed off as it is.
  //   //
  //   // The stacked markup and its CSS were already device-agnostic, so this only
  //   // switches which branch renders. See `stacked` in PaytableOverlay.
  //   stackedAlways: true,
  //   // Percent of the portrait band the picture fills, instead of being sized by
  //   // its width — see imgFillsBand in PaytableOverlay. 100 was a shade too big;
  //   // 90 leaves a little air at the edges. The band and the description line
  //   // are unaffected either way — only the picture inside changes.
  //   imgFillsBand: 90,
  //   // ONE image, not the split pot/coins pair — see the composite branch in
  //   // PaytableOverlay, which only runs when potAssetName is present.
  //   // Shared with the intro popup so the two screens show the same art.
  //   assetName: "startpage_image_2",
  //   imgWidth: {
  //     mobilePortrait: "75%",
  //     mobileLandscape: "55%",
  //     desktop: "55%",
  //   },
  //   descFontMul: 0.8,
  //   descWidthMultipliers: [15],
  //   // Same 1.5x portrait gaps as the Gamble slide — see the note there. All
  //   // four feature slides now breathe alike in the column.
  //   portraitBodyGapMul: 1.5,
  //   landscapeImgMul: 1.3,
  //   // Portrait only. The -3% landscape nudge was tuned for the row layout and
  //   // is wrong in the column — the image is centred in its own box now and
  //   // needs no pull.
  //   // ── NO portrait margins: they are what broke the shared line ──
  //   //
  //   // These three carried imgMarginTop -3.5% and their own descMarginTop
  //   // (-4% / -7.5% / -3.5%), while the Gamble slide carried neither. Every one
  //   // of them shifts this slide's image band or its description relative to
  //   // that slide, which is exactly the mismatch the ruler showed.
  //   //
  //   // The band (STACKED_PORTRAIT_IMG_BAND) is what positions these now, and it
  //   // only holds the four slides on one line if nothing nudges them afterwards.
  //   // Do not reintroduce a per-slide portrait margin here — tune the band.
  //   desc: "paytable_features_golden_reveal_description",
  // },
  // {
  //   subtitle: "paytable_features_mystery_reveal_title",
  //   // ── COLUMN in every layout: title, image, text ──
  //   //
  //   // Client reference, 2026-09-09. Collect / Double / Mystery all read as one
  //   // centred column on landscape and desktop too, not image-left / text-right.
  //   // The GAMBLE slide above keeps the row layout — it was signed off as it is.
  //   //
  //   // The stacked markup and its CSS were already device-agnostic, so this only
  //   // switches which branch renders. See `stacked` in PaytableOverlay.
  //   stackedAlways: true,
  //   // Percent of the portrait band the picture fills, instead of being sized by
  //   // its width — see imgFillsBand in PaytableOverlay. 100 was a shade too big;
  //   // 90 leaves a little air at the edges. The band and the description line
  //   // are unaffected either way — only the picture inside changes.
  //   imgFillsBand: 90,
  //   // ONE image, not the split pot/coins pair — see the composite branch in
  //   // PaytableOverlay, which only runs when potAssetName is present.
  //   // Shared with the intro popup so the two screens show the same art.
  //   assetName: "startpage_image_3",
  //   imgWidth: {
  //     mobilePortrait: "75%",
  //     mobileLandscape: "55%",
  //     desktop: "55%",
  //   },
  //   descFontMul: 0.8,
  //   descWidthMultipliers: [17],
  //   portraitImgMul: 1.1,
  //   // Same 1.5x portrait gaps as the Gamble slide — see the note there. All
  //   // four feature slides now breathe alike in the column.
  //   portraitBodyGapMul: 1.5,
  //   landscapeImgMul: 1.3,
  //   // Portrait only. The -3% landscape nudge was tuned for the row layout and
  //   // is wrong in the column — the image is centred in its own box now and
  //   // needs no pull.
  //   // ── NO portrait margins: they are what broke the shared line ──
  //   //
  //   // These three carried imgMarginTop -3.5% and their own descMarginTop
  //   // (-4% / -7.5% / -3.5%), while the Gamble slide carried neither. Every one
  //   // of them shifts this slide's image band or its description relative to
  //   // that slide, which is exactly the mismatch the ruler showed.
  //   //
  //   // The band (STACKED_PORTRAIT_IMG_BAND) is what positions these now, and it
  //   // only holds the four slides on one line if nothing nudges them afterwards.
  //   // Do not reintroduce a per-slide portrait margin here — tune the band.
  //   desc: "paytable_features_mystery_reveal_description",
  // },
];


// Slide 2: Coin + Jackpot Coins — 2-section symbol overview
export const SYMBOL_SLIDE_1_CONFIG = {
  descFontMul: 0.8,
  // ── One multiplier over BOTH symbols on this slide ──
  //
  // 0.85 -> 0.81, a shade under 5%: the wild and the scatter were reading
  // large against everything else in the paytable.
  //
  // It is applied in two places that must agree — the fit that decides how much
  // height the images claim, and the render that draws them — so the
  // description automatically takes back the space this gives up. Per-symbol
  // `imgScale` below is untouched, so the two stay in proportion with each
  // other.
  imgScaleMul: 0.81,
  // Per-section fraction (slide has 2 stacked sections in portrait)
  imgFraction: { portrait: 0.5, landscape: 0.38 },
  // Uniform image bump on standard mobile landscape — per-symbol imgScale ratios unchanged
  standardMobileLandscapeImgFractionMul: 1.18,
};

// ── The WILD and the SCATTER, side by side ──
//
// This was the coin / jackpot-coin pair, which is Zeus's. The two special
// symbols take its place: they are the two that do not pay as a line, so one
// screen for both reads better than a page each.
//
// IMAGES ONLY so far. The titles and descriptions are still the coin pair's
// keys, deliberately: Mad Labs has no copy for these two yet, and a key with no
// translation renders as the key itself on the slide. Wrong words beat a raw
// `paytable_symbols_scatter_title` in front of a player.
//
// The art is a flat image named `paytable_` plus the symbol's own `texture`
// key. The prefix matters: the bare name belongs to the SPINE — see the
// paytable block in assets.js.
//
// The scales are the pair's own, left as they were: they were tuned for two
// images of roughly this footprint, and a symbol is close enough that starting
// from them beats starting from nothing.
export const SYMBOL_SLIDE_1 = [
  {
    // subtitle: "paytable_symbols_wild_title",
    subtitle: "wild", // shared translation (2026-10-13)
    // assetName: "wild",
    // The symbol's own picture, made from its spine (SpineTextureExtractor —
    // as the symbols grid, slide 2), not a separate image file (Vladimir,
    // 2026-09-30: "use what we have").
    symbolName: "w0",
    // The SAME scales as the scatter below, and no visualScale — the two are
    // one pair of symbols and were drawing at visibly different sizes: this one
    // carried smaller per-device scales AND a 0.725 transform on top of them.
    imgScale: {
      mobilePortrait: 1.0,
      mobileLandscape: 1.0,
      desktop: 0.6,
    },
    // Landscape + desktop a bit bigger (2026-10-28), drawn larger only — the
    // box and the slide's fit are unchanged, so no text anywhere shrinks.
    visualScale: { mobilePortrait: 1.0, mobileLandscape: 1.12 },
    desc: "paytable_symbols_wild_description",
  },
  {
    // subtitle: "paytable_symbols_scatter_title",
    subtitle: "scatter", // shared translation (2026-10-13)
    // assetName: "scatter",
    // The symbol's own picture, made from its spine (SpineTextureExtractor —
    // as the symbols grid, slide 2), not a separate image file (Vladimir,
    // 2026-09-30: "use what we have").
    // The map is keyed by TEXTURE name — the scatter's is `sc`.
    symbolName: "sc",
    imgScale: {
      mobilePortrait: 1.0,
      mobileLandscape: 1.0,
      desktop: 0.6,
    },
    // The wild's 1.12 x 1.35: its picture carries glow rays round the door, so
    // the door drew ~1.35x smaller than the wild in the same box (2026-10-28,
    // measured on his screenshot) — this makes the two read as one size.
    visualScale: { mobilePortrait: 1.0, mobileLandscape: 1.51 },
    desc: "paytable_symbols_scatter_description",
  },
];

// ── The Hold & Win symbols slide (index 1) — slide 1's layout, three items ──
// Their pictures are made from their spines, as wild / scatter above: the map
// is keyed by TEXTURE name (SymbolData: `scr`, `chest` = ch silver,
// `golden_chest` = ch gold). Same scales as the wild / scatter pair.
export const SYMBOL_SLIDE_HNW = [
  {
    subtitle: "paytable_symbols_scarab_title",
    symbolName: "scr",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: the CAP of its picture, as a share of the slide body — the
    // three split what the texts leave, equally (spt-symbol-sections-even,
    // Loc QA 2026-11-02). Was a fixed 0.13, which overflowed on JA.
    // portraitImgShare: 0.13,
    portraitImgShare: 0.2,
    desc: "paytable_symbols_scarab_description",
  },
  {
    subtitle: "paytable_symbols_chest_title",
    symbolName: "chest",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: the CAP of its picture, as a share of the slide body — the
    // three split what the texts leave, equally (spt-symbol-sections-even,
    // Loc QA 2026-11-02). Was a fixed 0.13, which overflowed on JA.
    // portraitImgShare: 0.13,
    portraitImgShare: 0.2,
    desc: "paytable_symbols_chest_description",
  },
  {
    subtitle: "paytable_symbols_golden_chest_title",
    symbolName: "golden_chest",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: the CAP of its picture, as a share of the slide body — the
    // three split what the texts leave, equally (spt-symbol-sections-even,
    // Loc QA 2026-11-02). Was a fixed 0.13, which overflowed on JA.
    // portraitImgShare: 0.13,
    portraitImgShare: 0.2,
    desc: "paytable_symbols_golden_chest_description",
  },
];

// ── The coins slide (index 2) — slide 1's layout: the plain coin (`c`, its
// spine picture, no number) and the five jackpot coins joined into one
// picture (SpineTextureExtractor.getJackpotCoinsCompositeDataURL).
export const SYMBOL_SLIDE_COINS = [
  {
    subtitle: "paytable_symbols_coin_title",
    symbolName: "c",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: the picture's height as a share of the WHOLE slide body —
    // fixed, so the text length does not size it (a short text made the coin
    // huge, the long jackpot text the group tiny), and the sections take only
    // what their content needs, so everything fits (no overflow). The group a
    // bit taller: five coins.
    // portraitImgShare: 0.45,
    portraitImgShare: 0.19,
    desc: "paytable_symbols_coin_description",
  },
  {
    subtitle: "paytable_symbols_jackpot_title",
    composite: "jackpotCoins",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // portraitImgShare: 0.55,
    portraitImgShare: 0.26,
    desc: "paytable_symbols_jackpot_description",
  },
];

// ── Slide index 5 — "Unlock more rows" + "Royal Jackpot", slide 1's layout
// (a column on mobile, a row in landscape). Stand-in pictures from the spines
// until the art arrives: the scatter and the scarab (Vladimir, 2026-09-30).
// Its own slide, between Cleopatra's Respins and Free Spins.
export const SYMBOL_SLIDE_ROWS = [
  {
    subtitle: "paytable_features_rows_title",
    // symbolName: "sc",
    // The art (Vladimir, 2026-10-02): the intro's full 5x8 board.
    assetName: "startpage_image_5",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: this section's share of the slide's height against its
    // neighbour's (flex-grow) — the scatter bigger, the scarab smaller, every
    // gap still slide 1's (Vladimir, 2026-09-30).
    // portraitSectionGrow: 1.3,
    // portraitSectionGrow: 1.2,
    // 1.05 : 0.95 with the royal (Loc QA pass, 2026-10-28: "top one is bigger
    // … so they look similar and take almost equal space") — the pictures
    // fill what their sections leave, and the royal's logo is only 60% of its
    // art against this board's 84%, so it needs the nearer-even split.
    portraitSectionGrow: 1.05,
    // Landscape + desktop bigger (Loc QA pass, 2026-10-28: "awfully small"),
    // drawn larger only (the box and the slide's fit unchanged), grown UP
    // from the box's bottom so it stays clear of its title. The board is 84%
    // of this art's height.
    visualScale: { mobilePortrait: 1.0, mobileLandscape: 1.25 },
    visualOrigin: "center bottom",
    desc: "paytable_features_rows_description",
  },
  {
    subtitle: "paytable_features_royal_title",
    // symbolName: "scr",
    // // The art (Vladimir, 2026-10-02): the intro's full 5x8 board.
    // assetName: "startpage_image_5",
    // Its own paytable art (2026-10-02).
    assetName: "paytable_royal_jackpot",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // portraitSectionGrow: 0.7,
    // portraitSectionGrow: 0.8,
    portraitSectionGrow: 0.95, // see the rows item (2026-10-28)
    // The logo is only 60% of this art's height (26% glow above, 14% empty
    // below), so it needs more than the rows board to read as the same size:
    // x1.4 gives it the board's visual weight, centred on the same line.
    visualScale: { mobilePortrait: 1.0, mobileLandscape: 1.4 },
    visualOrigin: "center bottom",
    desc: "paytable_features_royal_description",
  },
];

// ── Slide index 6 — Free Spins, slide 1's layout with ONE item (a column on
// mobile, a row in landscape); the scatter's own picture (2026-09-30).
export const SYMBOL_SLIDE_FS = [
  {
    // subtitle: "paytable_features_free_spins_title",
    subtitle: "freespins", // shared translation (2026-10-13)
    symbolName: "sc",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    // Portrait: the picture's height as a share of the slide body — alone on
    // the slide it otherwise took all of it (Vladimir, 2026-09-30: "enormous").
    portraitImgShare: 0.34,
    desc: "paytable_features_free_spins_description",
  },
];

// Per-device image height fraction for symbol slides (rest goes to text)
// e.g. 0.60 = 60% image, 40% text
export const SYMBOL_IMG_RATIO = {
  mobilePortrait: 0.55,
  mobileLandscape: 0.5,
  desktop: 0.7,
};

// ---------------------------------------------------------------------------
// Slide 2: Zeus Symbols and POTs — 3-section with paired images
// ---------------------------------------------------------------------------
export const SYMBOL_SLIDE_2_CONFIG = {
  descFontMul: 0.8,
  descWidthMultipliers: [15, 15, 15],
  headerDescWidthMultiplier: 34,
  contentGapMul: 1.6,
  sectionRowGapMul: 1.6,
  sectionInnerGapMul: 0.8,
  contentSizingGapMul: 0.8,
  sectionRowSizingGapMul: 0.8,
  imgHeightMul: {
    mobilePortrait: 0.84,
    mobileSmallPortrait: 0.84,
    default: 0.65,
  },
  maxTextHeightMul: {
    mobilePortrait: 0.96,
    mobileSmallPortrait: 0.96,
    default: 0.85,
  },
  textMeasureWidthMul: {
    mobilePortrait: 1.0,
    mobileSmallPortrait: 1.0,
    default: 0.8,
  },
  portraitFixedFont: {
    mobilePortrait: true,
    mobileSmallPortrait: true,
    default: true,
  },
  portraitFontScale: {
    mobilePortrait: 1,
    mobileSmallPortrait: 1,
    default: 1,
  },
  // Landscape uniform-scale design. The slide is laid out ONCE at these fixed
  // px sizes (so the line-breaks are identical on every landscape device), then
  // the whole block is transform:scale()'d to fit the container. Tune these to
  // change the look; the scale just fits it to the available space.
  landscapeDesign: {
    width: 720, // fixed content width → deterministic wrapping
    headerDescFont: 17, // intro paragraph
    headerDescWidthPct: 0.9, // intro width as a fraction of `width`
    descFont: 17, // per-section descriptions (matches headerDescFont)
    imgH: 92, // section symbol height
    contentGap: 34, // vertical gap: intro↔sections (subtitle gap is standard)
    sectionRowGap: 34, // vertical gap between the two section rows
    sectionInnerGap: 12, // horizontal: grid column gap + image↔text gap
    scaleCap: 1.9, // max upscale so it can fill large screens
  },
};

export const SYMBOL_SLIDE_2_HEADER = {
  subtitle: "paytable_symbols_zeus_title",
  desc: "paytable_symbols_zeus_description",
};

export const SYMBOL_SLIDE_2 = [
  {
    assetName: "blue_zeus_symbol",
    imgScale: 1.0,
    desc: "paytable_symbols_zeus_collect_description",
  },
  {
    assetName: "green_zeus_symbol",
    imgScale: 1.0,
    desc: "paytable_symbols_zeus_double_description",
  },
  {
    assetName: "red_zeus_symbol",
    imgScale: 1.0,
    desc: "paytable_symbols_zeus_mystery_description",
  },
];

// Per-device image width fraction for slide 2 sections (image pair takes this %, text gets the rest)
export const SYMBOL_SLIDE_2_IMG_RATIO = {
  mobilePortrait: 0.32,
  mobileLandscape: 0.3,
  desktop: 0.3,
};

// Slide 4 (index 3): Regular symbols grid — landscape sizing tweaks
export const SYMBOL_SLIDE_4_CONFIG = {
  // Small mobile landscape needs a slight uniform shrink to avoid bottom-row clip
  smallMobileLandscapeScaleMul: 0.92,
  landscapeImgHeightMul: 0.72,
  landscapeFillScaleCap: 1.3,
  // portraitPvFontWeight is GONE. The pay values now take their weight from
  // .spt-grid-pv, which is a copy of .spt-feature-desc — one style for every
  // piece of body text in the paytable, so there is nothing to keep in step.
  portraitPvFontSizeOffset: 1,
};

// ---------------------------------------------------------------------------
// Chrome sizing — overlay shell elements
// ---------------------------------------------------------------------------
// Follows LogoLandscape.jsx DEVICE_CONFIG pattern.
// All ratios are multiples of `chromeBase`.
export const BASE_FONT_SIZE = 28;

export const CHROME_DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.55,
      arrowScale: 1.3,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.2,
      arrowScale: 1.3,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.95,
      arrowScale: 1.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.5,
      arrowScale: 1.5,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: { fontMultiplier: 1.4, arrowScale: 1.35 },
};

export const CHROME_RATIOS = {
  title: 2.0, // "Paytable" header h1
  subtitle: 1.5, // "Symbols" / "Feature" per-slide heading
  arrow: 3.3, // left/right chevron icons
  close: 1.25, // close button icon
  dot: 0.4, // pagination dot diameter
  separatorHeight: 0.14, // yellow separator line thickness
  topPad: 0.75, // top padding as ratio of chrome.base (scales with uiSymbolScale)
};

// Fixed carousel arrow layout — shared by PaytableSlider + PaytableOverlay slide content
export const PAYTABLE_ARROW = {
  insetMul: 0.65,
  iconWidthMul: 0.53,
  iconHeightMul: 0.8,
};

export const getPaytableArrowLayout = (chrome) => {
  const inset = Math.round(chrome.base * PAYTABLE_ARROW.insetMul);
  const iconWidth = Math.round(chrome.arrow * PAYTABLE_ARROW.iconWidthMul);
  const iconHeight = Math.round(chrome.arrow * PAYTABLE_ARROW.iconHeightMul);
  const padding = Math.round(chrome.base * 0.4);
  const safeGutter = inset + iconWidth + padding;
  return { inset, iconWidth, iconHeight, safeGutter };
};

// Tuned mobile landscape — shared chrome sizing (header, close, carousel arrows)
export const PAYTABLE_TUNED_LANDSCAPE_CHROME = {
  arrowIconMul: 0.82,
  sideSpreadMul: 2.6,
  headerTitleMul: 1.1,
  slideTitleMul: 1.15,
  // Desktop — header title + slide subtitle near the separator run a touch
  // smaller than the shared mobile-landscape muls.
  desktopHeaderTitleMul: 1.1,
  desktopSlideTitleMul: 1.1,
  // Wide desktop (≥ WIDE_DESKTOP_MIN_WIDTH, 2540px) — starts identical to
  // desktop; tune these independently to adjust sizes/paddings on large
  // monitors without affecting standard desktop.
  wideDesktopHeaderTitleMul: 1.3,
  wideDesktopSlideTitleMul: 1.3,
  wideDesktopShellTopPadMul: 1.7,
  wideDesktopShellBottomPadMul: 3.9,
  wideDesktopSeparatorMul: 1.0,
  wideDesktopSlideContentSpacedGapMul: 1.0,
  // Extra multiplier on the carousel arrow icon for wide desktop only.
  wideDesktopArrowMul: 1.185,
  // Extra downward nudge for the carousel arrows on wide desktop, as a % of
  // viewport height (added to the arrow's `top: calc(50% + …)`).
  wideDesktopArrowDownPct: 0.6,
  closeMul: 1.2,
  // Content width — a fixed multiple of uiSymbolSize per tier. Since the symbol
  // size is already fit to the viewport, this scales continuously and never
  // overflows, so no absolute cap is needed. Higher = wider slides. Seeded to
  // reproduce the previous absolute widths at reference resolutions; tune per
  // tier from the live values.
  standardMobileLandscapeContentWidthSymbolMul: 5.85,
  smallMobileLandscapeContentWidthSymbolMul: 6.4,
  tabletLandscapeContentWidthSymbolMul: 7.1,
  desktopContentWidthSymbolMul: 5.75,
  wideDesktopContentWidthSymbolMul: 7.15,
};

export const getPaytableTunedLandscapeSlideAreaPad = (
  viewportWidth,
  contentWidth,
) => Math.max(0, Math.round((viewportWidth - contentWidth) / 2));

export const getPaytableTunedLandscapeSideOffset = (chrome, slideAreaPad) => {
  const arrowLayout = getPaytableArrowLayout(chrome);
  const arrowIconWidth = Math.round(
    arrowLayout.iconWidth * PAYTABLE_TUNED_LANDSCAPE_CHROME.arrowIconMul,
  );
  const sideSpread = Math.round(
    chrome.base * PAYTABLE_TUNED_LANDSCAPE_CHROME.sideSpreadMul,
  );
  return Math.max(4, slideAreaPad - arrowIconWidth - sideSpread);
};

/**
 * Full overlay-shell layout — pure port of the equivalent computations in
 * PaytableOverlay.jsx (shellTopPad / shellBottomPad / shellContentWidth /
 * closeButton* / headerTitleFontSize / headerPaddingBottom).
 *
 * Exists so a sibling overlay (e.g. ReplayOverlay) can reproduce the paytable
 * shell — identical top/bottom padding, content width, and close-button
 * size/position — without re-deriving the branch tree and drifting out of sync.
 *
 * NOTE: headerTitleFontSize matters even for an overlay with no visible title:
 * the close button is absolutely positioned at top:50% of the header title row,
 * and that row's height is the title's line box. Reproduce the row height or the
 * X lands at a different Y than the paytable's.
 *
 * If PaytableOverlay's formulas change, mirror them here.
 */
export const getPaytableShellLayout = ({
  device,
  orientation,
  dimensions,
  uiSymbolScale,
  uiSymbolSize,
  isMobileSmall,
  isWideDesktop,
}) => {
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  const layoutDevice = isDesktop
    ? PixiSettings.DEVICE.MOBILE
    : getPaytableLayoutDevice(device);
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const config = getDeviceConfig(CHROME_DEVICE_CONFIG, layoutDevice, orientation);
  const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
  const chrome = {
    title: Math.round(base * CHROME_RATIOS.title),
    arrow: Math.round(base * CHROME_RATIOS.arrow * (config.arrowScale || 1.0)),
    close: Math.round(base * CHROME_RATIOS.close),
    topPad: Math.round(base * CHROME_RATIOS.topPad),
    base,
  };

  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isStandardMobile = device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const isStandardMobilePortrait =
    isStandardMobile && isPortrait && !isShortStandardPortrait;
  const isStandardMobileLandscape = isStandardMobile && !isPortrait;
  const isTabletPortrait = isTablet && isPortrait;
  const isTabletLandscape = isTablet && !isPortrait;
  const isSmallMobilePortrait =
    isPortrait && (isMobileSmall || isShortStandardPortrait);
  const isSmallMobileLandscape = isMobileSmall && !isPortrait;
  const isTunedStandardPortrait = isStandardMobilePortrait || isTabletPortrait;
  const isMirroredMobileLandscape =
    isStandardMobileLandscape || isTabletLandscape || isDesktop;
  const isTunedMobileLandscape =
    isMirroredMobileLandscape || isSmallMobileLandscape;
  const isTunedShellPortrait = isTunedStandardPortrait || isSmallMobilePortrait;

  const shellTopPad = isStandardMobilePortrait
    ? chrome.topPad * 7.5
    : isTabletPortrait
      ? chrome.topPad * 0.8
      : isTabletLandscape
        ? chrome.topPad * 1.4 * 6.5
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellTopPadMul
          : isDesktop
            ? chrome.topPad * 1.4
            : isTunedMobileLandscape
            ? chrome.topPad * 1.6
            : isSmallMobilePortrait
              ? chrome.topPad * 0.8
              : Math.round(chrome.topPad * 1.3);

  const shellBottomPad = isStandardMobilePortrait
    ? chrome.topPad * 7.75
    : isTabletPortrait
      ? chrome.topPad * 1.5
      : isTabletLandscape
        ? chrome.topPad * 3.3 * 3.35
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellBottomPadMul
          : isTunedMobileLandscape
          ? chrome.topPad * 3.3
          : isSmallMobilePortrait
            ? chrome.topPad * 1.5
            : isPortrait
              ? chrome.topPad * 3.8
              : chrome.topPad * 3.2;

  const contentWidthSymbolMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopContentWidthSymbolMul
    : isDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopContentWidthSymbolMul
    : isSmallMobileLandscape
      ? PAYTABLE_TUNED_LANDSCAPE_CHROME.smallMobileLandscapeContentWidthSymbolMul
      : isTabletLandscape
        ? PAYTABLE_TUNED_LANDSCAPE_CHROME.tabletLandscapeContentWidthSymbolMul
        : PAYTABLE_TUNED_LANDSCAPE_CHROME.standardMobileLandscapeContentWidthSymbolMul;
  const tunedMobileLandscapeShellWidth = isTunedMobileLandscape
    ? Math.round(uiSymbolSize * contentWidthSymbolMul)
    : null;

  const tabletPortraitContentWidth = isTabletPortrait
    ? dimensions.width * 0.5
    : null;

  const shellContentWidth = isStandardMobilePortrait
    ? dimensions.width * 0.75
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isSmallMobilePortrait
        ? dimensions.width * 0.8
        : (tunedMobileLandscapeShellWidth ?? undefined);

  const separatorLineWidth = isStandardMobilePortrait
    ? Math.min(chrome.title * 12, dimensions.width * 0.95)
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isTunedMobileLandscape
        ? tunedMobileLandscapeShellWidth
        : isPortrait
          ? Math.min(chrome.title * 12, dimensions.width * 0.92)
          : Math.min(chrome.title * 16, dimensions.width * 0.65);

  const closeButtonSize = isTunedMobileLandscape
    ? Math.round(chrome.close * PAYTABLE_TUNED_LANDSCAPE_CHROME.closeMul)
    : chrome.close;

  const tunedLandscapeSlideAreaPad =
    isTunedMobileLandscape && shellContentWidth != null
      ? getPaytableTunedLandscapeSlideAreaPad(dimensions.width, shellContentWidth)
      : null;
  const tunedLandscapeChromeSideOffset =
    tunedLandscapeSlideAreaPad != null
      ? getPaytableTunedLandscapeSideOffset(chrome, tunedLandscapeSlideAreaPad)
      : null;

  const closeButtonRight = isPortrait
    ? Math.max(6, Math.round(chrome.close * 0.12))
    : tunedLandscapeChromeSideOffset != null
      ? Math.max(4, tunedLandscapeChromeSideOffset - Math.round(chrome.close * 0.35))
      : shellContentWidth != null
        ? Math.max(
            4,
            Math.round((dimensions.width - shellContentWidth) / 2) -
              Math.round(chrome.close * 0.8),
          )
        : Math.round(
            (dimensions.width - separatorLineWidth) / 2 - chrome.close * 3.0,
          );

  const headerTitleFontSize = isTunedStandardPortrait
    ? Math.round(chrome.title * 0.94)
    : isWideDesktop
      ? Math.round(
          chrome.title *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopHeaderTitleMul,
        )
      : isDesktop
      ? Math.round(
          chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopHeaderTitleMul,
        )
      : isTunedMobileLandscape
        ? Math.round(
            chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.headerTitleMul,
          )
        : chrome.title;

  const linePad = Math.round(chrome.title * 0.5);
  const headerPaddingBottom = isTunedShellPortrait
    ? 0
    : Math.round(linePad * 0.25);

  return {
    shellTopPad,
    shellBottomPad,
    shellContentWidth,
    closeButtonSize,
    closeButtonRight,
    headerTitleFontSize,
    headerPaddingBottom,
  };
};

// ---------------------------------------------------------------------------
// Per-slide content layout
// ---------------------------------------------------------------------------
// Each slide type declares its structure. Actual pixel sizes are computed
// at render time from `contentHeight`.
//
// imageFraction:  portion of card height allocated to the image
// fontToImage:    pay-value font size as fraction of image size
// descToImage:    description text as fraction of image size
// nameToImage:    symbol name label as fraction of image size
// gapFraction:    inter-card gap as fraction of available grid height
export const SLIDE_LAYOUT = {
  // Slide 1: Wild + Scatter — large special symbol cards
  specialSymbols: {
    columns: { portrait: 1, landscape: 2 },
    rows: { portrait: 2, landscape: 1 },
    imageFraction: 0.5,
    fontToImage: 0.13,
    descToImage: 0.11,
    nameToImage: 0.14,
    gapFraction: 0.04,
  },
  // Slide 2: Regular symbols (hp, mp, lp) — grid layout
  regularSymbols: {
    columns: { portrait: 2, landscape: 4 },
    rows: { portrait: 4, landscape: 2 },
    imageFraction: 0.55,
    fontToImage: 0.14,
    gapFraction: 0.03,
  },
  // Slides 3–5: Feature explanation — always row, 50/50 image + text
  feature: {
    fontToImage: 0.08,
  },
  // Slides 6–7: Payline diagrams — single large image
  paylines: {
    imageFraction: 0.9,
  },
};

// ---------------------------------------------------------------------------
// Content max width per device/orientation (fraction of viewport width)
// ---------------------------------------------------------------------------
export const CONTENT_WIDTH_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.92,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.75,
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.75,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.92,
  },
  [PixiSettings.DEVICE.DESKTOP]: 0.88,
};

// ---------------------------------------------------------------------------
// Description font sizing — per-device/orientation ratios
// baseMul: base font multiplier (chrome.base * baseMul)
// ratio:   scales both height and width of text container (1.0 = full, 0.9 = 90%)
// ---------------------------------------------------------------------------
export const DESC_FONT_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 1.0, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 1.0, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 0.85, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.DESKTOP]: { baseMul: 0.95, ratio: 0.75 },
};

// ---------------------------------------------------------------------------
// Global description font multiplier — layered ON TOP of each slide's own
// descFontMul, applied to the STARTING font size fed into each slide's
// auto-fit measurement (the shrink-to-fit loop for feature slides, the
// design-font input to the ratio-scale for Wild/Coin/Zeus slides). A language
// that needs the full shrink still gets it — this only lets a locale/tier
// that leaves slack render a touch bigger before that fit math runs.
// Applies to every slide with a description EXCEPT the Regular Grid (slide 3,
// pay-value numbers — a separate, unrelated font path) and the Paylines
// slide (image only, no text). Keep values close to 1.0 — this should read
// as a 1-2px nudge, never a visible jump.
// ---------------------------------------------------------------------------
export const GLOBAL_DESC_FONT_MULTIPLIER = {
  default: {
    mobileSmallPortrait: 1.0,
    mobileSmallLandscape: 1.0,
    mobilePortrait: 1.0,
    mobileLandscape: 1.0,
    tabletPortrait: 1.0,
    tabletLandscape: 1.0,
    desktop: 1.0,
    wideDesktop: 1.0,
  },
  // Locale-specific overrides, keyed by i18n language code (e.g. "ru").
  // Falls back to `default` for any locale or tier not listed here.
  ru: {
    mobileSmallPortrait: 1.065,
    mobileSmallLandscape: 1.1,
    mobilePortrait: 1.065,
    mobileLandscape: 1.05,
    desktop: 1.1,
    wideDesktop: 1.2,
  },
  en: {
    mobileSmallPortrait: 1.065,
    mobileSmallLandscape: 1.1,
    mobilePortrait: 1.065,
    mobileLandscape: 1.05,
    desktop: 1.1,
    wideDesktop: 1.2,
  },
};

/**
 * Resolve the global description font multiplier for a device/orientation
 * tier + locale. Tier keys mirror PaytableOverlay's own device/orientation
 * booleans exactly — no separate small/wide desktop split beyond what that
 * file already computes via isWideDesktop.
 * @param {string} tierKey - one of: mobileSmallPortrait, mobileSmallLandscape,
 *   mobilePortrait, mobileLandscape, tabletPortrait, tabletLandscape, desktop, wideDesktop
 * @param {string} [locale] - i18n language code (e.g. "ru")
 * @returns {number} multiplier, defaults to 1.0 if unset
 */
export const getGlobalDescFontMultiplier = (tierKey, locale) => {
  const localeConfig = locale && GLOBAL_DESC_FONT_MULTIPLIER[locale];
  return (
    localeConfig?.[tierKey] ??
    GLOBAL_DESC_FONT_MULTIPLIER.default[tierKey] ??
    1.0
  );
};

// ---------------------------------------------------------------------------
// Per-slide content scale (fraction of available width/height)
// ---------------------------------------------------------------------------
// Fine-tune how much of the content area each slide type uses per device.
export const SLIDE_CONTENT_SCALE = {
  specialSymbols: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 0.95 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.9, h: 0.9 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 0.95 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 0.9 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.85, h: 0.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.75, h: 0.85 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.65, h: 0.8 },
  },
  regularSymbols: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.9, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.85, h: 1.0 },
  },
  feature: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.95, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.9, h: 1.0 },
  },
  paylines: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.75, h: 1.0 },
  },
};

// ---------------------------------------------------------------------------
// Game Rules overlay — padding layout (AutoplayPopup fullscreen pattern)
// ---------------------------------------------------------------------------
export const RULES_OVERLAY_PADDING_CONFIG = {
  basePadding: { horizontal: 28, vertical: 40 },
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, top: 1.5, bottom: 0.5 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.8, top: 2.6, bottom: 0.75 },
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, v: 6.75 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 5.0, top: 2.75, bottom: 0.75 },
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
    },
  },
};

/**
 * Resolve rules overlay content box from padding multipliers.
 * Mirrors AutoplayPopup fullscreen viewportConfig math.
 */
export const resolveRulesLayout = ({
  dimensions,
  toolbarWidth,
  device,
  orientation,
  isMobileSmall,
  uiSymbolScale,
  headerHeight = 0,
}) => {
  const configDevice = isMobileSmall
    ? PixiSettings.DEVICE.MOBILE_SMALL
    : device;
  const tierConfig = getDeviceConfig(
    RULES_OVERLAY_PADDING_CONFIG,
    configDevice,
    orientation,
  );
  const multipliers = tierConfig?.paddingMultiplier || { h: 1.0, v: 1.0 };

  const isSmallMobileLandscape =
    isMobileSmall && orientation === PixiSettings.ORIENTATION.LANDSCAPE;
  const containerWidth = isSmallMobileLandscape
    ? dimensions.width
    : Math.min(dimensions.width, toolbarWidth || dimensions.width);

  const paddingH = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.horizontal *
      multipliers.h *
      uiSymbolScale,
  );
  const topMult = multipliers.top ?? multipliers.v ?? 1.0;
  const bottomMult = multipliers.bottom ?? multipliers.v ?? 1.0;
  const paddingTop = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.vertical * topMult * uiSymbolScale,
  );
  const paddingBottom = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.vertical *
      bottomMult *
      uiSymbolScale,
  );

  const contentWidth = containerWidth - paddingH * 2;
  const contentHeight = dimensions.height - paddingTop - paddingBottom;
  const availableTextHeight = Math.max(0, contentHeight - headerHeight);

  const tierLabel = `${configDevice}_${orientation}`;

  return {
    tierLabel,
    paddingH,
    paddingTop,
    paddingBottom,
    containerWidth,
    contentWidth,
    contentHeight,
    availableTextHeight,
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Paytable layout treats tablet as mobile — chrome/content scale via uiSymbolScale.
 */
export const getPaytableLayoutDevice = (device) =>
  device === PixiSettings.DEVICE.TABLET ? PixiSettings.DEVICE.MOBILE : device;

/**
 * Resolve device config — Desktop has no orientation nesting.
 * Falls back to Desktop config if device/orientation combo not found.
 */
export const getDeviceConfig = (configObj, device, orientation) => {
  const layoutDevice = getPaytableLayoutDevice(device);
  if (layoutDevice === PixiSettings.DEVICE.DESKTOP) {
    return configObj[layoutDevice] || configObj[PixiSettings.DEVICE.DESKTOP];
  }
  return (
    configObj[layoutDevice]?.[orientation] ||
    configObj[PixiSettings.DEVICE.DESKTOP]
  );
};

/**
 * Resolve per-device/orientation image width from a widthMap.
 */
/**
 * ── THREE KEYS. That is the whole contract ──
 *
 *   mobilePortrait   every portrait device, phone AND tablet
 *   desktop          desktop only
 *   mobileLandscape  every other landscape, phone AND tablet
 *
 * `getPaytableLayoutDevice` folds TABLET into MOBILE, so a `tabletPortrait` or
 * `tabletLandscape` key in a map read through here is never looked at. Sixteen
 * of them were removed on 2026-09-09 — they looked like tuned values and had
 * never been drawn once, which is worse than no value at all.
 *
 * Not every tablet key in this file is dead: the global description-font tiers
 * are resolved by `descFontTierKey` in PaytableOverlay, which DOES distinguish
 * tablet. Check which resolver reads a map before adding or removing a key.
 */
export const getImgWidth = (widthMap, device, isPortrait) => {
  if (!widthMap) return undefined;
  const layoutDevice = getPaytableLayoutDevice(device);
  const isDesktop = layoutDevice === PixiSettings.DEVICE.DESKTOP;
  if (isPortrait) {
    return widthMap.mobilePortrait;
  }
  if (isDesktop) return widthMap.desktop;
  return widthMap.mobileLandscape;
};

/** Resolve per-breakpoint Zeus slide layout value from a keyed map. */
export const getZeusSlideLayoutVal = (
  valueMap,
  { isStandardMobilePortrait, isSmallMobilePortrait },
  fallback,
) => {
  if (valueMap == null || typeof valueMap !== "object") {
    return valueMap ?? fallback;
  }
  if (isStandardMobilePortrait && valueMap.mobilePortrait != null) {
    return valueMap.mobilePortrait;
  }
  if (isSmallMobilePortrait && valueMap.mobileSmallPortrait != null) {
    return valueMap.mobileSmallPortrait;
  }
  return valueMap.default ?? fallback;
};

/**
 * Reusable off-screen canvas for text measurement (avoids creating new ones).
 */
let _measureCanvas = null;
export const getMeasureCanvas = () => {
  if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
  return _measureCanvas;
};

/**
 * Measure wrapped text height using canvas text measurement.
 * Shared by all slide sizing computations to avoid duplication.
 */
export const measureTextHeight = (ctx, text, fontSize, maxWidth) => {
  ctx.font = `400 ${fontSize}px 'Noto Sans', sans-serif`;
  const lineHeight = fontSize * 1.4;

  const measureWrappedSegment = (segment) => {
    if (!segment) return 1;

    const words = segment.split(/\s+/).filter(Boolean);
    if (words.length === 0) return 1;

    if (words.length === 1) {
      let lines = 1;
      let lineW = 0;
      for (const char of segment) {
        const charW = ctx.measureText(char).width;
        if (lineW + charW > maxWidth && lineW > 0) {
          lines++;
          lineW = charW;
        } else {
          lineW += charW;
        }
      }
      return lines;
    }

    let lines = 1;
    let lineW = 0;
    for (const word of words) {
      const wordW = ctx.measureText(`${word} `).width;
      if (lineW + wordW > maxWidth && lineW > 0) {
        lines++;
        lineW = wordW;
      } else {
        lineW += wordW;
      }
    }
    return lines;
  };

  let totalLines = 0;
  for (const segment of text.split("\n")) {
    totalLines += measureWrappedSegment(segment);
  }
  return totalLines * lineHeight;
};

/**
 * Group symbols by pay category (wild, scatter, hp, mp, lp).
 */
export const getSymbolGroups = () => {
  const groups = { wild: [], scatter: [], hp: [], mp: [], lp: [] };
  Object.entries(symbolData).forEach(([id, data]) => {
    const numId = parseInt(id);
    if (data.symbolPay === "wild") groups.wild.push({ id: numId, ...data });
    else if (data.symbolPay === "scatter")
      groups.scatter.push({ id: numId, ...data });
    else if (data.symbolPay === "hp") groups.hp.push({ id: numId, ...data });
    else if (data.symbolPay === "mp") groups.mp.push({ id: numId, ...data });
    else if (data.symbolPay === "lp") groups.lp.push({ id: numId, ...data });
  });
  return groups;
};

/**
 * Get formatted pay values for a symbol (descending by count, non-zero only).
 * Matches MiniPaytable: payout = symbolValue × (totalBet / baseBet).
 * baseBet is the API line/unit factor (e.g. 25) — without it values are N× too high.
 */
export const getPayValues = (paytable, symbolId, bet, baseBet = 1) => {
  const payValues = paytable?.[symbolId] || [];
  const safeBaseBet = baseBet || 1;
  const betUnit = (bet || 1) / safeBaseBet;
  return payValues
    .map((value, idx) => ({ value, count: idx + 1 }))
    .reverse()
    .filter((item) => item.value !== 0)
    .map((item) => ({
      count: item.count,
      payout: formatToCustomLocale(betUnit * item.value),
    }));
};

/**
 * Convert a PIXI texture (from Assets cache) to a data URL for use in <img>.
 */
export const pixiTextureToDataURL = (texture) => {
  if (!texture || !texture.baseTexture?.resource?.source) return "";
  try {
    const frame = texture.frame;
    const source = texture.baseTexture.resource.source;
    const canvas = document.createElement("canvas");
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      source,
      frame.x,
      frame.y,
      frame.width,
      frame.height,
      0,
      0,
      frame.width,
      frame.height,
    );
    return canvas.toDataURL("image/webp");
  } catch {
    return "";
  }
};

// ---------------------------------------------------------------------------
// Module-level caches for extracted textures (survive re-mounts)
// ---------------------------------------------------------------------------
// Using getter/setter pattern for cleaner module boundaries.

let _cachedSymbolImages = null;
let _cachedFeatureImages = null;
let _cachedPaylineImages = { portrait: null, landscape: null };

export const getCachedSymbolImages = () => _cachedSymbolImages;
export const setCachedSymbolImages = (v) => {
  _cachedSymbolImages = v;
};

export const getCachedFeatureImages = () => _cachedFeatureImages;
export const setCachedFeatureImages = (v) => {
  _cachedFeatureImages = v;
};

export const getCachedPaylineImages = () => _cachedPaylineImages;
export const setCachedPaylineImage = (key, v) => {
  _cachedPaylineImages[key] = v;
};
